import React, { useState, useRef, useEffect, useCallback } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';

const EarningsHeader = ({ showWatchlist, onToggleWatchlist, loggedInUser, totalCount, isLoading, industries, selectedIndustries, onToggleIndustry, onClearIndustries }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (dropdownOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [dropdownOpen]);

  const filteredIndustries = search
    ? industries.filter(i => i.toLowerCase().includes(search.toLowerCase()))
    : industries;

  const handleToggle = useCallback((industry) => {
    onToggleIndustry(industry);
  }, [onToggleIndustry]);

  return (
    <div className="sticky top-0 z-10 border-b border-gray-200/60 backdrop-blur-md bg-white/80">
      <div className="w-full md:w-3/4 mx-auto px-4 py-3">
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-sm">
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'white' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.2, margin: 0 }}>Earnings Calendar</h1>
              <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.2, margin: 0 }}>
                {isLoading ? 'Loading...' : totalCount > 0 ? `${totalCount.toLocaleString()} stocks reporting this week` : 'Next 7 days'}
              </p>
            </div>
          </div>

          {loggedInUser && (
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => showWatchlist && onToggleWatchlist()}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                  !showWatchlist ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => !showWatchlist && onToggleWatchlist()}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                  showWatchlist ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Watchlist
              </button>
            </div>
          )}
        </div>

        {/* Industry filter row */}
        {industries.length > 0 && (
          <div style={{ marginTop: 10, position: 'relative' }} ref={dropdownRef}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {/* Filter button */}
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setSearch(''); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  border: '1px solid #e5e7eb', cursor: 'pointer',
                  background: selectedIndustries.length > 0 ? '#1f2937' : '#fff',
                  color: selectedIndustries.length > 0 ? '#fff' : '#374151',
                }}
              >
                <FilterListIcon sx={{ fontSize: 14 }} />
                Industry{selectedIndustries.length > 0 ? ` (${selectedIndustries.length})` : ''}
              </button>

              {/* Selected industry chips */}
              {selectedIndustries.map((industry) => (
                <span
                  key={industry}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                    background: '#f3f4f6', color: '#374151', cursor: 'pointer',
                  }}
                  onClick={() => handleToggle(industry)}
                >
                  {industry}
                  <CloseIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                </span>
              ))}

              {/* Clear all */}
              {selectedIndustries.length > 0 && (
                <button
                  onClick={onClearIndustries}
                  style={{
                    fontSize: 11, fontWeight: 600, color: '#6b7280', background: 'none',
                    border: 'none', cursor: 'pointer', padding: '4px 2px', textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)', zIndex: 50,
                maxHeight: 320, display: 'flex', flexDirection: 'column',
              }}>
                {/* Search input */}
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <SearchIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                  <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search industries..."
                    style={{
                      flex: 1, border: 'none', outline: 'none', fontSize: 13,
                      color: '#111827', background: 'transparent',
                    }}
                  />
                  {search && (
                    <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                      <CloseIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                    </button>
                  )}
                </div>

                {/* Industry list */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {filteredIndustries.length === 0 && (
                    <div style={{ padding: '16px 12px', textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
                      No industries match "{search}"
                    </div>
                  )}
                  {filteredIndustries.map((industry) => {
                    const isSelected = selectedIndustries.includes(industry);
                    return (
                      <button
                        key={industry}
                        onClick={() => handleToggle(industry)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '8px 12px', fontSize: 12, textAlign: 'left',
                          border: 'none', cursor: 'pointer',
                          background: isSelected ? '#f0fdf4' : 'transparent',
                          color: isSelected ? '#047857' : '#374151',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.target.style.background = '#f9fafb'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.target.style.background = 'transparent'; }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: isSelected ? 'none' : '1.5px solid #d1d5db',
                          background: isSelected ? '#059669' : '#fff',
                        }}>
                          {isSelected && <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />}
                        </div>
                        <span style={{ flex: 1 }}>{industry}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EarningsHeader;
